import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { ChartEngine, Region, Layer, GraphElement, ChartObject, MaskConfig } from '../core/ChartEngine';
import { ChartOptions } from '../Chart';

export const ChartRenderer: React.FC<{ 
  engine: ChartEngine; 
  options: ChartOptions;
  onStepChange?: (step: number) => void;
}> = ({ engine, options, onStepChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 1. 위치 및 크기 파싱 헬퍼
    const parsePos = (val: any, base: number) => {
      if (typeof val === 'string' && val.endsWith('%')) {
        return (parseFloat(val) / 100) * base;
      }
      return parseFloat(val) || 0;
    };

    // 2. 수식 및 윈도우 계산 로직
    const calculateTransform = (data: any[], el: GraphElement) => {
      if (!el.transform) return data;
      const { expression, window } = el.transform;

      return data.map((d, i, arr) => {
        let computedValue: number | null = null;
        if (expression) {
          try {
            const fields = Object.keys(d);
            const values = Object.values(d);
            // eslint-disable-next-line no-new-func
            const calcFn = new Function(...fields, `return ${expression}`);
            computedValue = calcFn(...values);
          } catch (e) { console.error("수식 오류:", e); }
        }
        if (window && i >= window.period - 1) {
          const slice = arr.slice(i - window.period + 1, i + 1);
          const values = slice.map(item => +item[window.field] || 0);
          if (window.func === 'avg') computedValue = d3.mean(values) || 0;
          else if (window.func === 'sum') computedValue = d3.sum(values);
          else if (window.func === 'max') computedValue = d3.max(values) || 0;
        }
        return { ...d, __val__: computedValue };
      });
    };

    // 3. 축 렌더링 로직
    const renderAxes = (svg: any, layer: Layer, width: number, height: number, yScale: any, xScale: any) => {
      const axesGroup = svg.selectAll('.axes-group').data([null]).join('g').attr('class', 'axes-group');
      const yAxisConfig = layer.axes?.yAxis;
      const xAxisConfig = layer.axes?.xAxis;

      if (yAxisConfig?.showGrid) {
        axesGroup.selectAll('.grid-line-y').data(yScale.ticks(5)).join('line').attr('class', 'grid-line-y')
          .attr('x1', 0).attr('x2', width).attr('y1', (d: any) => yScale(d)).attr('y2', (d: any) => yScale(d))
          .attr('stroke', '#e0e0e0').attr('stroke-dasharray', '2,2');
      }

      if (xAxisConfig) {
        const xAxis = d3.axisBottom(xScale).tickSize(0).tickPadding(8);
        axesGroup.selectAll('.x-axis').data([null]).join('g').attr('class', 'x-axis')
          .attr('transform', `translate(0, ${height})`).call(xAxis as any)
          .call((g: any) => g.select('.domain').attr('stroke', '#ccc'))
          .selectAll('text').style('fill', '#666').style('font-size', '11px');
      }

      if (yAxisConfig) {
        const orient = yAxisConfig.orient === 'left' ? d3.axisLeft(yScale) : d3.axisRight(yScale);
        axesGroup.selectAll('.y-axis').data([null]).join('g').attr('class', 'y-axis')
          .attr('transform', `translate(${yAxisConfig.orient === 'left' ? 0 : width}, 0)`)
          .call(orient.tickSize(0).tickPadding(8) as any)
          .call((g: any) => {
            g.select('.domain').remove();
            g.selectAll('text').style('fill', '#666').style('font-size', '11px');
          });
      }
    };

    // 4. 메인 업데이트 로직
    const updateChart = (regions: Region[]) => {
      const container = d3.select(containerRef.current);
      if (!containerRef.current) return;
      
      const globalData = engine.getData();

      container.style('width', options.width).style('height', options.height)
        .style('background-color', options.backgroundColor || '#fff')
        .style('border', options.border ? `${options.border.width ?? '1px'} solid ${options.border.color ?? '#ccc'}` : 'none')
        .style('border-radius', options.border?.radius || '0px')
        .style('position', 'relative').style('overflow', 'hidden');

      const regionSelection = container.selectAll<HTMLDivElement, Region>('.region-container')
        .data(regions, d => d.id).join('div').attr('class', 'region-container')
        .style('position', 'absolute')
        .style('width', d => d.width)
        .style('height', d => d.height)
        .style('top', d => d.top)
        .style('left', d => d.left)
        .each(function(d) {
          if (d.style) {
            const el = d3.select(this);
            Object.entries(d.style).forEach(([key, value]) => {
              const kebabKey = key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
              el.style(kebabKey, value as string);
            });
          }
        });

      regionSelection.each(function (regionData) {
        const regionNode = d3.select(this);
        const layers = regionNode.selectAll<HTMLDivElement, Layer>('.layer-container')
          .data(regionData.layers, l => l.id)
          .join('div').attr('class', 'layer-container')
          .style('position', 'absolute')
          .style('width', d => typeof d.width === 'number' ? `${d.width}px` : (d.width || '100%'))
          .style('height', d => typeof d.height === 'number' ? `${d.height}px` : (d.height || '100%'))
          .style('top', d => typeof d.top === 'number' ? `${d.top}px` : (d.top || '0px'))
          .style('left', d => typeof d.left === 'number' ? `${d.left}px` : (d.left || '0px'))
          .style('z-index', d => d.zIndex || 0);

        layers.each(function (layerData) {
          const node = this as HTMLElement;
          const width = node.clientWidth;
          const height = node.clientHeight;
          if (width === 0 || height === 0) return;

          const processedElements = layerData.elements.map((el: GraphElement) => ({
            el, data: calculateTransform(globalData, el)
          }));

          const xField = processedElements[0]?.el.mapping.xAxis || 'baseDd';
          const xScale = d3.scaleBand().domain(globalData.map(d => d[xField])).range([0, width]).padding(0.2);

          const yConf = layerData.axes?.yAxis;
          const getValFromSource = (source: any, type: 'min' | 'max'): number => {
            if (typeof source === 'number') return source;
            const vals: number[] = [];
            processedElements.forEach(({ el, data }) => {
              data.forEach((d: any) => {
                let targets: string[] = [];
                if (source === 'all' || !source) {
                  targets = el.type === 'candle' ? [el.mapping.high, el.mapping.low] : [el.transform ? '__val__' : el.mapping.y];
                } else if (Array.isArray(source)) targets = source;
                targets.forEach(f => {
                  const v = f === '__val__' ? d.__val__ : d[f];
                  if (v !== null && !isNaN(v)) vals.push(+v);
                });
              });
            });
            return type === 'min' ? (d3.min(vals) ?? 0) : (d3.max(vals) ?? 100);
          };

          const rawMin = getValFromSource(yConf?.yMinAxisSource, 'min');
          const rawMax = getValFromSource(yConf?.yMaxAxisSource, 'max');
          const pad = (rawMax - rawMin) * 0.05;
          const yMin = typeof yConf?.yMinAxisSource === 'number' ? rawMin : rawMin - pad;
          const yMax = typeof yConf?.yMaxAxisSource === 'number' ? rawMax : rawMax + pad;
          const yScale = d3.scaleLinear().domain([yMin, yMax]).range([height, 0]);

          const svg = d3.select(this).selectAll<SVGSVGElement, any>('svg.graph-svg')
            .data([null]).join('svg').attr('class', 'graph-svg')
            .style('width', '100%').style('height', '100%').style('overflow', 'visible');

          // Masking
          const defs = svg.selectAll('defs').data([null]).join('defs');
          const currentMasks = layerData.masks || [];
          const maskTag = defs.selectAll(`#layer-mask-${layerData.id}`)
            .data([layerData.id]).join('mask').attr('id', d => `layer-mask-${d}`);

          maskTag.selectAll('.m-bg').data([null]).join('rect').attr('class', 'm-bg')
            .attr('width', '100%').attr('height', '100%').attr('fill', 'white');

          maskTag.selectAll('.m-hole').data(currentMasks, (m: any) => m.id)
            .join('g').attr('class', 'm-hole')
            .each(function(m: MaskConfig) {
              const g = d3.select(this);
              g.selectAll('*').remove();
              const cx = parsePos(m.x, width);
              const cy = parsePos(m.y, height);
              if (m.type === 'circle') {
                g.append('circle').attr('cx', cx).attr('cy', cy).attr('r', parsePos(m.radius || 0, Math.min(width, height))).attr('fill', 'black');
              } else if (m.type === 'rect') {
                const w = parsePos(m.width || 0, width);
                const h = parsePos(m.height || 0, height);
                g.append('rect').attr('x', cx - w / 2).attr('y', cy - h / 2).attr('width', w).attr('height', h).attr('rx', m.borderRadius || 0).attr('fill', 'black');
              }
            });

          // [핵심 수정] 축 중복 방지: yAxis 설정이 있고, 메인 데이터가 있는 레이어에서만 축 렌더링
          if (layerData.axes && (layerData.elements.length > 0 || layerData.id === 'price-layer')) {
            renderAxes(svg, layerData, width, height, yScale, xScale);
          } else {
            svg.selectAll('.axes-group').remove();
          }

          const mainGroup = svg.selectAll('.graph-main-group').data([null]).join('g').attr('class', 'graph-main-group');
          
          processedElements.forEach(({ el, data }: any) => {
            const g = mainGroup.selectAll(`.group-${el.id}`).data([null]).join('g').attr('class', `group-${el.id}`);
            if (el.type === 'candle') {
              g.selectAll('.wick').data(data).join('line').attr('class', 'wick')
                .attr('x1', (d: any) => xScale(d[el.mapping.xAxis])! + xScale.bandwidth()/2)
                .attr('x2', (d: any) => xScale(d[el.mapping.xAxis])! + xScale.bandwidth()/2)
                .attr('y1', (d: any) => yScale(d[el.mapping.high])).attr('y2', (d: any) => yScale(d[el.mapping.low]))
                .attr('stroke', (d: any) => d[el.mapping.open] > d[el.mapping.close] ? el.style.negativeColor : el.style.positiveColor);
              g.selectAll('.body').data(data).join('rect').attr('class', 'body')
                .attr('x', (d: any) => xScale(d[el.mapping.xAxis])!).attr('y', (d: any) => yScale(Math.max(d[el.mapping.open], d[el.mapping.close])))
                .attr('width', xScale.bandwidth()).attr('height', (d: any) => Math.max(1, Math.abs(yScale(d[el.mapping.open]) - yScale(d[el.mapping.close]))))
                .attr('fill', (d: any) => d[el.mapping.open] > d[el.mapping.close] ? el.style.negativeColor : el.style.positiveColor);
            }
          });

          // Object Layer
          const objectGroup = svg.selectAll('.object-layer-group').data([null]).join('g').attr('class', 'object-layer-group');
          objectGroup.selectAll('.draw-item').data(layerData.objects || [], (d: any) => d.id)
            .join(enter => enter.append('g').attr('class', 'draw-item').call(g => g.append('rect')))
            .each(function(d: ChartObject) {
              const s = d.style;
              d3.select(this).select('rect')
                .attr('x', parsePos(d.x, width)).attr('y', parsePos(d.y, height))
                .attr('width', parsePos(d.width || 0, width)).attr('height', parsePos(d.height || 0, height))
                .attr('fill', s.fill || 'transparent').attr('opacity', s.opacity ?? 1)
                .attr('mask', d.id.includes('overlay') && currentMasks.length > 0 ? `url(#layer-mask-${layerData.id})` : null);
            });

          // HTML Layer (Boxes)
          const htmlLayer = d3.select(this).selectAll<HTMLDivElement, any>('.html-layer')
            .data([null]).join('div').attr('class', 'html-layer')
            .style('position', 'absolute').style('top', 0).style('left', 0)
            .style('width', '100%').style('height', '100%').style('pointer-events', 'none').style('z-index', 999);

          const nextBtnBoxes = (layerData as any).htmlBoxes || [];

          htmlLayer.selectAll<HTMLDivElement, any>('.next-btn-box')
            .data(nextBtnBoxes, (d: any) => d.id)
            .join('div').attr('class', 'next-btn-box')
            .style('position', 'absolute').style('pointer-events', 'auto')
            .style('left', (d: any) => typeof d.x === 'string' ? d.x : `${d.x}px`)
            .style('top', (d: any) => typeof d.y === 'string' ? d.y : `${d.y}px`)
            .style('transform', 'translate(-50%, -50%)')
            .each(function(d: any) {
              const box = d3.select(this);
              box.selectAll('*').remove();
              const applyStyles = (sel: any, styles: any) => {
                if (!styles) return;
                Object.entries(styles).forEach(([k, v]) => sel.style(k.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`), v as string));
              };

              box.style('background-color', '#fff').style('padding', '16px').style('box-shadow', '0 4px 12px rgba(0,0,0,0.15)').style('border-radius', '8px').style('display', 'flex').style('flex-direction', 'column');
              applyStyles(box, d.BoxStyle);

              box.append('div').style('font-weight', 'bold').style('font-size', '16px').style('margin-bottom', '8px').text(d.title).call(s => applyStyles(s, d.titleStyle));

              const contentDiv = box.append('div').style('flex', '1').style('margin-bottom', '16px');
              const lines: string[] = Array.isArray(d.content) ? d.content : [d.content];
              lines.forEach((t: string) => contentDiv.append('div').text(t).style('margin-bottom', '4px').call(s => applyStyles(s, d.contentStyle)));

              box.append('div').style('display', 'flex').style('justify-content', 'flex-end')
                .append('button').text(d.btnContent || '다음').style('padding', '8px 16px').style('cursor', 'pointer').style('border', 'none').style('border-radius', '4px').style('background-color', '#0378D8').style('color', '#fff')
                .call(s => applyStyles(s, d.btnStyle))
                .on('click', () => onStepChange?.(d.nextStep));
            });
        });
      });
    };

    const unsubscribe = engine.subscribe(() => updateChart(engine.getSnapshot()));
    updateChart(engine.getSnapshot());
    return () => unsubscribe();
  }, [engine, options, onStepChange]);

  return <div ref={containerRef} style={{ position: 'relative' }} />;
};